import React, { useState } from 'react';
import { Contact } from '../types/Contact';
import { StateAbbreviations } from '../types/States';
import Col100 from './Col100';
import Header from './Header';
import Processing from './Processing';
import { dynamicsService } from '../services/DynamicsService';

type Props = {
  onComplete: ((
    sameAsPrevious: boolean,
    primaryContactId: string,
    primaryContact?: Contact
  ) => void);
  client: Contact;
  clientId: string;
  checkoutId: string;
};
export default function PrimaryContact(props: Props): JSX.Element {
  const { onComplete, client, clientId, checkoutId } = props;
  const [inputs, setInputs] = useState({
    sameAsPrevious: false,
    id: "",
    firstName: "",
    lastName: "",
    email: "",
    address1: "",
    address2: "",
    city: "",
    state: "MD",
    zipcode: "",
    telephone: "",
    relationship: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string>();
  const [primaryContactID, setPrimaryContactID] = useState<string>();

  const [loading, setLoading] = useState(true);

  // Accessors
  const { sameAsPrevious, firstName, lastName, email, address1, address2, city, state, zipcode, telephone, relationship, id } = inputs;
  // Event Handlers
  function handleChange(e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) {
    const { name, value } = e.target;
    if (e.target instanceof HTMLInputElement && e.target.type === "checkbox") {
      const checkbox = e.target;
      setInputs(inputs => ({
        ...inputs,
        [name]: checkbox.checked
      }));
    }
    else {
      setInputs(inputs => ({ ...inputs, [name]: value }));
    }
  }
  function handleSubmit(e: React.FormEvent) {
    console.log("handleSubmit", e);
    e.preventDefault();
    setSubmitting(true);
    if (sameAsPrevious) {
      onComplete(sameAsPrevious, "");
      return;
    }
    // TODO Validation

    // TODO submit

    // Advance to Step 3 by calling the onComplete callback handler in App
    let contact = {
      firstName,
      lastName,
      address1,
      address2,
      city,
      state,
      zipcode,
      telephone,
      email,
      relationship,
      id
    };

    dynamicsService.createOrUpdatePrimaryContact(contact, clientId)
      .then((clientContact) => {
        console.log(clientContact);
        setPrimaryContactID(clientContact);
        onComplete(sameAsPrevious, clientContact, contact);
        setLoading(false);
      }).catch((reason) => setError(reason));
  }
  if (error) {
    return <Col100>
      <Header>Error!</Header>
      <div className="elementor-element elementor-widget elementor-widget-text-editor" data-element_type="widget" data-widget_type="text-editor.default">
        <div className="elementor-widget-container">
          <div className="elementor-text-editor elementor-clearfix">
            <p>
              We're sorry-- but there was a problem storing your
              primary contact's information. Please go back, and try again.
            </p>
          </div>
        </div>
      </div>
    </Col100>;
  }

  if (submitting) {
    return (<Col100><Processing /></Col100>);
  }
  return <Col100>
      <Header>Primary Contact</Header>
      <p>
        If you are scheduling this SafeHaven 360 Plan for someone else as
        their caregiver, partner, or spouse, please let us know how to contact
        you. If you are scheduling this SafeHaven 360 Plan for yourself,
        please select "Same as Previous Screen"
      </p>
      <form onSubmit={handleSubmit} className="order-form elementor-form" id="orderForm" name="OrderForm">
        <div className="elementor-form-fields-wrapper elementor-labels-">
          <div className="elementor-field-type-checkbox elementor-field-group elementor-column elementor-col-100">
            <label htmlFor="sameAsPrevious" className="elementor-field-label elementor-screen-only">
              Same as Previous Screen
            </label>
            <div className="elementor-field-subgroup  ">
              <span className="elementor-field-option cb-label">
                <input type="checkbox" value="Same as Previous Screen" id="sameAsPrevious" name="sameAsPrevious" onChange={handleChange} />
                <label htmlFor="sameAsPrevious" style={{ marginLeft: ".25rem" }}>
                  Same as Previous Screen
                </label>
              </span>
            </div>
          </div>
          <div className="d-flex">
            <div className="flex-grow-1 mr-1">
              <label htmlFor="firstName" className="">
                First Name
              </label>
              {sameAsPrevious && <input size={1} disabled={true} readOnly={true} type="text" id="firstName" name="firstName" className="input da-plugin" placeholder="First Name" required={false} aria-required="true" value={client.firstName} />}
              {!sameAsPrevious && <input size={1} type="text" id="firstName" name="firstName" className="input da-plugin" placeholder="First Name" required={true} aria-required="true" value={firstName} onChange={handleChange} />}
            </div>
            <div className="flex-grow-1">
              <label htmlFor="lastName" className="">
                Last Name
              </label>
              {sameAsPrevious && <input size={1} type="text" disabled={true} readOnly={true} name="lastName" id="lastName" className="input da-plugin" placeholder="Last Name" aria-required="true" value={client.lastName} />}
              {!sameAsPrevious && <input size={1} type="text" name="lastName" id="lastName" className="input da-plugin" placeholder="Last Name" required={true} aria-required="true" value={lastName} onChange={handleChange} />}
            </div>
          </div>
          <div className="d-flex mt-2">
            <div className="flex-grow-1">
              <label htmlFor="address1" className="">
                Street Address
              </label>
              {sameAsPrevious && <input size={1} type="text" disabled={true} readOnly={true} name="address1" id="address1" className="input da-plugin" placeholder="Street Address" aria-required="true" value={client.address1} />}
              {!sameAsPrevious && <input size={1} type="text" name="address1" id="address1" className="input da-plugin" placeholder="Street Address" required={true} aria-required="true" value={address1} onChange={handleChange} />}
            </div>
          </div>
          <div className="d-flex mt-1">
            <div className="flex-grow-1">
              {sameAsPrevious && <input size={1} type="text" disabled={true} readOnly={true} name="address2" id="address2" className="input da-plugin" placeholder="Apartment, Suite, Etc." aria-required="true" value={client.address2} />}
              {!sameAsPrevious && <input size={1} type="text" name="address2" id="address2" className="input da-plugin" placeholder="Apartment, Suite, Etc." required={false} aria-required="true" value={address2} onChange={handleChange} />}
            </div>
          </div>
          <div className="d-flex mt-2">
            <div className="flex-grow-1 mr-1">
              <label htmlFor="city" className="">
                City
              </label>
              {sameAsPrevious && <input size={1} type="text" disabled={true} readOnly={true} name="city" id="city" className="input da-plugin" placeholder="City" aria-required="true" value={client.city} />}
              {!sameAsPrevious && <input size={1} type="text" name="city" id="city" className="input da-plugin" placeholder="City" required={true} aria-required="true" value={city} onChange={handleChange} />}
            </div>
            <div className="mr-1">
              <label htmlFor="state" className="">
                State
              </label>
              <div className="">
                {sameAsPrevious && <select name="state" id="state" disabled={true} className="input da-plugin" aria-required="true" value={client.state}>
                    <option key={client.state} value={client.state}>
                      {client.state}
                    </option>
                  </select>}
                {!sameAsPrevious && <select name="state" id="state" className="input da-plugin" required={true} aria-required="true" onChange={handleChange} value={state}>
                    <option value="">State</option>
                    {Object.keys(StateAbbreviations()).map(state =>
                      <option key={state} value={state}>
                        {state}
                      </option>
                    )}
                  </select>}
              </div>
            </div>
            <div className="">
              <label htmlFor="zipcode" className="">
                Zipcode
              </label>
              <div>
                {sameAsPrevious && <input type="number" name="zipcode" id="zipcode" className="input da-plugin" placeholder="Zipcode" disabled={true} readOnly={true} aria-required="true" min="0" max="99999" value={client.zipcode} />}
                {!sameAsPrevious && <input type="number" name="zipcode" id="zipcode" className="input da-plugin" placeholder="Zipcode" required={true} aria-required="true" min="0" max="99999" onChange={handleChange} value={zipcode} />}
              </div>
            </div>
          </div>
          <div className="d-flex">
            <div className="flex-grow-1 mr-1">
              <label htmlFor="email" className="">
                Email
              </label>
              {sameAsPrevious && <input size={1} type="email" name="email" id="email" className="input da-plugin" placeholder="Email" disabled={true} readOnly={true} aria-required="true" value={client.email} />}
              {!sameAsPrevious && <input size={1} type="email" name="email" id="email" className="input da-plugin" placeholder="Email" required={true} aria-required="true" value={email} onChange={handleChange} />}
            </div>
            <div className="flex-grow-1">
              <label htmlFor="form-field-message" className="">
                Telephone
              </label>
              {sameAsPrevious && <input size={1} type="text" name="telephone" id="form-field-message" className="input da-plugin" placeholder="Telephone" disabled={true} readOnly={true} value={client.telephone} />}
              {!sameAsPrevious && <input size={1} type="text" name="telephone" id="form-field-message" className="input da-plugin" placeholder="Telephone" required={true} aria-required="true" value={telephone} onChange={handleChange} />}
            </div>
          </div>
          {!sameAsPrevious && <div className="d-flex">
              <div className="flex-grow-1">
                <label htmlFor="relationship" className="">
                  Relationship
                </label>
                <select name="relationship" id="relationship" className="input da-plugin" required={true} aria-required="true" onChange={handleChange} value={relationship}>
                  <option value="">Select Relationship</option>
                  <option value="562950000">Spouse</option>
                  <option value="876960000">Child</option>
                  <option value="876960002">Friend</option>
                  <option value="876960003">Legal Counsel</option>
                  <option value="876960004">Primary Caregiver</option>
                  <option value="876960005">Physician/Provider</option>
                  <option value="562950001">Other</option>
                </select>
              </div>
            </div>}
          <div className="mt-2 d-flex">
            <button type="submit" className="et_pb_button et_pb_button_0 et_pb_bg_layout_light flex-grow-1">
                <span className="elementor-button-text mr-1">Next</span>
                  <i aria-hidden="true" className="fas fa-angle-right" />
            </button>
          </div>
        </div>
      </form>
    </Col100>;
}